import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ToastProvider } from './components/providers/ToastProvider';
import { PrivateRoute, AdminRoute, RoleGuard } from './components/guards/RouteGuards';
import DashboardLayout from './components/layout/DashboardLayout';
import Login from './pages/Login';
import SSOCallback from './pages/SSOCallback';
import Home from './pages/Home';
import Dashboard from './pages/Dashboard';
import Courses from './pages/Courses';
import CourseDetail from './pages/CourseDetail';
import Tasks from './pages/Tasks';
import TaskDetail from './pages/TaskDetail';
import AIHub from './pages/AIHub';
import BusinessProfile from './pages/BusinessProfile';
import Activation from './pages/Activation';
import OneStepPoster from './pages/OneStepPoster';
import Settings from './pages/Settings';
import PWARegister from './components/providers/PWARegister';
import Maintenance from './pages/Maintenance';
import { useAutoUpdate } from './hooks/useAutoUpdate';

// Admin Page Imports
import AdminUsers from './pages/AdminUsers';
import AdminStudents from './pages/AdminStudents';
import AdminStudentDetail from './pages/AdminStudentDetail';
import AdminStaffAssessment from './pages/AdminStaffAssessment';
import AdminStaffDetail from './pages/AdminStaffDetail';
import AdminBadges from './pages/AdminBadges';
import AdminAIUsage from './pages/AdminAIUsage';
import AdminVIPWaitlist from './pages/AdminVIPWaitlist';
import AdminSubscriptions from './pages/AdminSubscriptions';
import AdminSystemUtilities from './pages/AdminSystemUtilities';

// Coach Page Imports
import TaskApprovals from './pages/TaskApprovals';
import Discussions from './pages/Discussions';
import StudentAssessment from './pages/StudentAssessment';
import StudentAssessmentDetail from './pages/StudentAssessmentDetail';
import CoachDesk from './pages/CoachDesk';
import CoachCourses from './pages/CoachCourses';
import CoachCourseDetail from './pages/CoachCourseDetail';
import CoachTasks from './pages/CoachTasks';

// Marketing Page Imports
import MarketingConversions from './pages/MarketingConversions';
import MarketingPool from './pages/MarketingPool';
import { PlanProvider } from './context/PlanContext';

export default function App() {
  useAutoUpdate();
  return (
    <PlanProvider>
      <ToastProvider>
        <AuthProvider>
        <PWARegister />
        <Router>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/auth/callback" element={<SSOCallback />} />
            <Route path="/maintenance" element={<Maintenance />} />

            {/* Secure Core Dashboard Routes */}
            <Route element={<PrivateRoute><DashboardLayout /></PrivateRoute>}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/courses" element={<Courses />} />
              <Route path="/courses/:id" element={<CourseDetail />} />
              <Route path="/tasks" element={<Tasks />} />
              <Route path="/tasks/:id" element={<TaskDetail />} />
              <Route path="/ai-hub" element={<AIHub />} />
              <Route path="/profile" element={<Navigate to="/settings" replace />} />
              <Route path="/profile/business" element={<BusinessProfile />} />
              <Route path="/settings" element={<Settings />} />

              {/* Marketing Routes */}
              <Route path="/marketing/conversions" element={<MarketingConversions />} />
              <Route path="/marketing/pool" element={<MarketingPool />} />
            </Route>

            {/* Standalone full-viewport Pages (that render their own Header & Sidebar) */}
            <Route path="/activation" element={<PrivateRoute><Activation /></PrivateRoute>} />
            <Route path="/one-step-poster" element={<PrivateRoute><OneStepPoster /></PrivateRoute>} />
            <Route path="/discussions" element={<PrivateRoute><Discussions /></PrivateRoute>} />
            <Route path="/taskapprovals" element={<PrivateRoute><TaskApprovals /></PrivateRoute>} />

            {/* Coach Exclusive Access Channels */}
            <Route path="/coach/student-assessment" element={<RoleGuard allowedRoles={['ADMIN', 'COACH', 'COACHING_ASSOCIATE']}><StudentAssessment /></RoleGuard>} />
            <Route path="/coach/student-assessment/:id" element={<RoleGuard allowedRoles={['ADMIN', 'COACH', 'COACHING_ASSOCIATE']}><StudentAssessmentDetail /></RoleGuard>} />
            <Route path="/coach/desk" element={<RoleGuard allowedRoles={['ADMIN', 'COACH', 'COACHING_ASSOCIATE']}><CoachDesk /></RoleGuard>} />
            <Route path="/coach/courses" element={<RoleGuard allowedRoles={['ADMIN', 'COACH', 'COACHING_ASSOCIATE']}><CoachCourses /></RoleGuard>} />
            <Route path="/coach/courses/:id" element={<RoleGuard allowedRoles={['ADMIN', 'COACH', 'COACHING_ASSOCIATE']}><CoachCourseDetail /></RoleGuard>} />
            <Route path="/coach/tasks" element={<RoleGuard allowedRoles={['ADMIN', 'COACH', 'COACHING_ASSOCIATE']}><CoachTasks /></RoleGuard>} />

            {/* Admin Exclusive Access Channels */}
            <Route path="/admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
            <Route path="/admin/students" element={<AdminRoute><AdminStudents /></AdminRoute>} />
            <Route path="/admin/students/:id" element={<AdminRoute><AdminStudentDetail /></AdminRoute>} />
            <Route path="/admin/staff-assessment" element={<AdminRoute><AdminStaffAssessment /></AdminRoute>} />
            <Route path="/admin/staff-assessment/:id" element={<AdminRoute><AdminStaffDetail /></AdminRoute>} />
            <Route path="/admin/badges" element={<AdminRoute><AdminBadges /></AdminRoute>} />
            <Route path="/admin/ai-usage" element={<AdminRoute><AdminAIUsage /></AdminRoute>} />
            <Route path="/admin/vip-waitlist" element={<AdminRoute><AdminVIPWaitlist /></AdminRoute>} />
            <Route path="/admin/subscriptions" element={<AdminRoute><AdminSubscriptions /></AdminRoute>} />
            <Route path="/admin/utilities" element={<AdminRoute><AdminSystemUtilities /></AdminRoute>} />

            {/* Global Catch-all redirecting to Dashboard */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ToastProvider>
    </PlanProvider>
  );
}
export type { App };
